import React, { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { shortTime } from '../../utils/time'
import {
  LiveIcon,
} from '../icons'
import { trackEvent } from '../../utils/dtmHelpers'
import Popup from '../GuideCard/Popup'
import getImageRenditions from '../../utils/getImageRenditions'
import { GUIDE_CARD_RENDITIONS, POSTER_RENDITIONS } from '../../constants'
import styles from './styles'
import Image from '../Image'

const ScheduleCard = ({
  isOnNow,
  isOnNext,
  isOnLater,
  title,
  summary,
  startTime,
  sportsExclusive,
  endTime,
  image,
  modalImage,
  link,
  position,
  cardTitle,
  channel,
}) => {
  const channels = useSelector(state => state.liveChannels?.data?.results || [])
  const currentChannel = channels.find(ch => ch.channel === channel)
  const channelImageUrl = currentChannel?.custom?.iosPosterImage?._filepath ?? null
  const channelImage = channelImageUrl ? {
    _filepath: channelImageUrl,
    ...getImageRenditions(channelImageUrl, GUIDE_CARD_RENDITIONS),
  } : null

  const posterChannelImage = channelImageUrl ? {
    _filepath: channelImageUrl,
    ...getImageRenditions(channelImageUrl, POSTER_RENDITIONS),
  } : null

  const titleRef = useRef(null)
  const [titleLines, setTitleLines] = useState(2)

  useEffect(() => {
    if (titleRef.current) {
      const titleHeight = titleRef.current.offsetHeight
      const lineHeight = parseInt(window.getComputedStyle(titleRef.current).lineHeight, 10)
      const lines = Math.round(titleHeight / lineHeight)
      setTitleLines(lines)
    }
  }, [title])

  const [showPopup, setShowPopup] = useState(false)
  const props = {
    title,
    description: summary,
    startTime,
    endTime,
    image: Object.keys(modalImage || {}).length === 0 ? channelImage : modalImage,
    link,
  }
  const titleArray = title?.split(':')

  const togglePopUp = () => {
    if (!showPopup) {
      trackEvent('homepageClick', { item: `Live page - now / next,99,${position},${cardTitle}` })
    }
    setShowPopup(!showPopup)
  }

  return (
    title && (
      <div
        className={`ScheduleCard ${isOnNow ? 'on-now' : ''} ${isOnLater ? 'on-later' : ''}`}
        onClick={togglePopUp}
        onKeyDown={togglePopUp}
        role="button"
        tabIndex={0}
      >
        <div className="ScheduleCard-inner-wrapper">
          { isOnNow && !isOnNext && !isOnLater
    && (
      <div className="schedule on-now">
        <p className="pre-title on-now" data-testid="on-now"><LiveIcon height={10} width={10} />Live</p>
      </div>
    )}
          { isOnNext && !isOnNow && !isOnLater
    && (
      <div className="schedule on-next">
        <p className="pre-title on-next" data-testid="on-next">On next</p>
      </div>
    )}
          { isOnLater && !isOnNext && !isOnNow
    && (
      <div className="schedule on-next">
        <p className="pre-title on-next" data-testid="on-later">On later</p>
      </div>
    )}
          <div className="main-content">
            <div className="card-image">
              <Image
                className="now-next-image"
                style={{
                  borderRadius: '4px', width: '87px', height: '116px', objectFit: 'contain',
                }}
                image={Object.keys(image).length === 0 ? posterChannelImage : image}
              />
            </div>
            <div className="text-wrapper">
              <p className="time">{shortTime(startTime)} until {shortTime(endTime)}</p>
              <h3 ref={titleRef} className="title">
                {titleArray.length === 2 ? (
                  <>
                    <span className={`title-main ${sportsExclusive && 'psports'}`}>{titleArray[0]}: </span>
                    <span className="title-subtitle">{titleArray[1]}</span>
                  </>
                ) : (
                  <span className={`title-main ${sportsExclusive && 'psports'}`}>{title}</span>
                )}
              </h3>
              <p className="summary" style={{ WebkitLineClamp: titleLines === 1 ? 3 : 2 }}>{summary}</p>
            </div>
          </div>
        </div>
        {/* eslint-disable-next-line max-len */}
        {startTime && endTime && <Popup showPopup={showPopup} popupProps={props} onClose={togglePopUp} />}
        <style jsx>{ styles }</style>
      </div>
    )
  )
}

ScheduleCard.propTypes = {
  isOnNow: PropTypes.bool,
  isOnNext: PropTypes.bool,
  isOnLater: PropTypes.bool,
  title: PropTypes.string.isRequired,
  summary: PropTypes.string.isRequired,
  startTime: PropTypes.string.isRequired,
  endTime: PropTypes.string.isRequired,
  modalImage: PropTypes.object,
  link: PropTypes.string,
  position: PropTypes.number,
  cardTitle: PropTypes.string,
  image: PropTypes.object,
  sportsExclusive: PropTypes.bool,
  channel: PropTypes.string.isRequired,
}

ScheduleCard.defaultProps = {
  isOnNow: false,
  isOnNext: false,
  isOnLater: false,
  link: '',
  modalImage: {},
  position: 1,
  cardTitle: '',
  image: {},
  sportsExclusive: false,
}

export default ScheduleCard
